import {
  Checkbox,
  Divider,
  List,
  ListItemButton,
  ListItemButtonProps,
  ListItemIcon,
  ListItemText,
  Typography
} from "@mui/material";
import React, { Dispatch } from "react";

export interface CustomListItem<T> extends ListItemProps {
  data: T;
}

interface ListItemProps extends ListItemButtonProps {
  disableRipple?: true;
  hideCheckBoxes?: boolean;
  label?: string;
  selected: boolean;
}

export const ListItem: React.FC<ListItemProps> = ({
  disableRipple,
  hideCheckBoxes,
  label,
  selected,
  ...rest
}: ListItemProps) => {
  return (
    <ListItemButton dense selected={selected} {...rest}>
      {!hideCheckBoxes && (
        <ListItemIcon>
          <Checkbox edge="start" checked={selected} tabIndex={-1} disableRipple={disableRipple} />
        </ListItemIcon>
      )}
      <ListItemText primary={label || "Unidentified Label"} />
    </ListItemButton>
  );
};

interface Props<T> {
  /** Flag to disable mouse events and style the component as inactive. */
  disabled?: boolean;
  /** Flag to optionally disable the ripple animation triggered on item selection. */
  disableRipple?: true;
  /** Flag to optionally hide the check boxes, and use only item styling to indicate selected items.
   * Unused if `listItem` is provided.
   */
  hideCheckBoxes?: true;
  /** The items to render in the list. */
  items?: T[];
  /** Enable multi-select in the list. */
  multiSelect?: boolean;
  /** Custom component render for the individual list items. */
  renderListItem?: (props: CustomListItem<T>) => React.ReactNode;
  /** Title to render at the top of the component.*/
  title?: string;
  isValueSelected: (value: T) => boolean;
  /** Callback for when the selection state of the list changes. */
  onChange?: Dispatch<T[]>;
}

/** A helper component that allows you to select one or more items from a defined collection. */
const SelectList = <T,>({
  disabled,
  disableRipple,
  hideCheckBoxes,
  items,
  multiSelect = false,
  renderListItem = ({ data, ...rest }) => <ListItem label={String(data)} {...rest} />,
  title,
  isValueSelected,
  onChange
}: Props<T>): JSX.Element => {
  const updateSelection = (index: number) => () => {
    if (!items || !onChange) return;
    if (!multiSelect) return onChange([items[index]]);
    const newActiveItems = items.reduce((collector, value, valueIndex) => {
      const isClickedValue = index === valueIndex;
      if (isValueSelected(value) && !isClickedValue) collector.push(value);
      else if (isClickedValue) collector.push(value);
      return collector;
    }, [] as T[]);
    onChange(newActiveItems);
  };

  return (
    <>
      {title && (
        <Typography variant="h6" gutterBottom>
          {title}
        </Typography>
      )}
      <List
        disablePadding
        sx={theme => ({
          backgroundColor: theme.palette.background.paper,
          borderColor: theme.palette.action.disabled,
          borderRadius: theme.shape.borderRadius,
          width: "100%",
          border: "solid 1px",
          ...(disabled && { opacity: 0.6, pointerEvents: "none" })
        })}
      >
        {items?.length ? (
          items.map((itemValue, index) => {
            const selected = isValueSelected(itemValue);
            return (
              <span key={index}>
                {renderListItem({
                  label: String(itemValue),
                  disableRipple,
                  hideCheckBoxes,
                  data: itemValue,
                  selected,
                  dense: true,
                  onClick: updateSelection(index)
                })}
                {index !== items.length - 1 && <Divider component="li" />}
              </span>
            );
          })
        ) : (
          <ListItemButton>
            <Typography variant="body1">No Items</Typography>
          </ListItemButton>
        )}
      </List>
    </>
  );
};

export default SelectList;
